/* eslint-disable camelcase */
/**
 * Gym-wide aggregation — fetches all gyms in parallel and aggregates into
 * the gym-wide totals + cross-gym ranked coach list that the PDF report
 * surfaces. The per-coach view shown above on the same dashboard is a
 * single-coach filter on the same underlying data; this is the superset.
 */

import * as React from "react";
import {
  ICoach,
  ICoachPerformanceResponse,
  ICoachPerformanceError
} from "./coach-performance.types";
import {
  getShowRate,
  getConversionRate,
  getFreeSessionsBooked,
  getFreeSessionsAttended,
  getFreeSessionsConverted
} from "./use-coach-performance";

const API_URL = process.env.REACT_APP_COACH_PERF_API_URL || "";
const API_KEY = process.env.REACT_APP_COACH_PERF_API_KEY || "";

export const ADMIN_SUBDOMAINS = [
  "startingstrengthcentennial",
  "startingstrengthsunsetvalley",
  "startingstrengthaustin"
];

export interface IRankedCoach {
  coach: ICoach;
  subdomain: string;
  gymName: string;
  score: number | null;
  insufficientData: boolean;
}

export interface IGymTotals {
  bookings: number;
  shows: number;
  conversions: number;
  showRate: number | null;
  conversionRate: number | null;
}

export interface IAdminAggregate {
  /** Per-gym responses, in the order ADMIN_SUBDOMAINS is declared. */
  perGym: ICoachPerformanceResponse[];
  /** Every coach across every gym, ranked by composite score. */
  ranked: IRankedCoach[];
  /** Aggregated across all gyms in scope. */
  totals: IGymTotals;
  /** Month in YYYY-MM, echoed back for convenience. */
  month: string;
}

export interface IUseAdminFree30 {
  data: IAdminAggregate | null;
  errors: Array<{ subdomain: string; error: ICoachPerformanceError }>;
  loading: boolean;
  configured: boolean;
  month: string;
  setMonth: (m: string) => void;
}

const MIN_BOOKINGS_FOR_RANK = 3;

/** Composite score: (conversion × 0.6) + (show × 0.4), 0–100. */
export function compositeScore(coach: ICoach): number | null {
  const conv = getConversionRate(coach);
  const show = getShowRate(coach);
  if (conv === null && show === null) return null;
  const c = conv ?? 0;
  const s = show ?? 0;
  return Math.round((c * 0.6 + s * 0.4) * 1000) / 10;
}

export function rankCoaches(
  perGym: ICoachPerformanceResponse[]
): IRankedCoach[] {
  const all: IRankedCoach[] = perGym.flatMap(resp =>
    resp.coaches.map(c => ({
      coach: c,
      subdomain: resp.gym.subdomain,
      gymName: resp.gym.name,
      score: compositeScore(c),
      insufficientData: getFreeSessionsBooked(c) < MIN_BOOKINGS_FOR_RANK
    }))
  );

  return all.sort((a, b) => {
    // Coaches with insufficient data sink to the bottom; within each
    // group, sort by score descending.
    if (a.insufficientData !== b.insufficientData) {
      return a.insufficientData ? 1 : -1;
    }
    const sa = a.score ?? -Infinity;
    const sb = b.score ?? -Infinity;
    return sb - sa;
  });
}

export function gymTotals(perGym: ICoachPerformanceResponse[]): IGymTotals {
  let bookings = 0;
  let shows = 0;
  let conversions = 0;
  perGym.forEach(resp => {
    resp.coaches.forEach(c => {
      bookings += getFreeSessionsBooked(c);
      shows += getFreeSessionsAttended(c);
      conversions += getFreeSessionsConverted(c);
    });
  });
  return {
    bookings,
    shows,
    conversions,
    showRate: bookings === 0 ? null : shows / bookings,
    conversionRate: shows === 0 ? null : conversions / shows
  };
}

async function fetchGym(
  subdomain: string,
  month: string
): Promise<ICoachPerformanceResponse> {
  const url = `${API_URL}?subdomain=${encodeURIComponent(
    subdomain
  )}&month=${encodeURIComponent(month)}`;
  const res = await fetch(url, { headers: { "X-API-Key": API_KEY } });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err: ICoachPerformanceError = body || {
      error: "UNKNOWN_ERROR",
      message: `HTTP ${res.status}`
    };
    throw err;
  }
  return body as ICoachPerformanceResponse;
}

export function useAdminFree30(initialMonth: string): IUseAdminFree30 {
  const configured = Boolean(API_URL && API_KEY);
  const [month, setMonth] = React.useState(initialMonth);
  const [data, setData] = React.useState<IAdminAggregate | null>(null);
  const [errors, setErrors] = React.useState<
    Array<{ subdomain: string; error: ICoachPerformanceError }>
  >([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!configured) return undefined;
    let cancelled = false;
    setLoading(true);

    Promise.allSettled(ADMIN_SUBDOMAINS.map(sub => fetchGym(sub, month))).then(
      results => {
        if (cancelled) return;
        const ok: ICoachPerformanceResponse[] = [];
        const errs: Array<{
          subdomain: string;
          error: ICoachPerformanceError;
        }> = [];
        results.forEach((r, i) => {
          if (r.status === "fulfilled") {
            ok.push(r.value);
          } else {
            const reason = r.reason as ICoachPerformanceError | Error;
            const normalized: ICoachPerformanceError =
              reason && (reason as ICoachPerformanceError).error
                ? (reason as ICoachPerformanceError)
                : {
                    error: "FETCH_FAILED",
                    message: (reason as Error).message || "Network error"
                  };
            errs.push({ subdomain: ADMIN_SUBDOMAINS[i], error: normalized });
          }
        });
        setData({
          perGym: ok,
          ranked: rankCoaches(ok),
          totals: gymTotals(ok),
          month
        });
        setErrors(errs);
        setLoading(false);
      }
    );

    return () => {
      cancelled = true;
    };
  }, [configured, month]);

  return { data, errors, loading, configured, month, setMonth };
}
